import { SyntheticEvent } from 'react';
import { Layout, Spacer, Text } from '@visiba-cortex/react-ui-std';
import { useService } from '@visiba/instantiation';
import { AnalyticAction } from '@application/analytic/analytic.service';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import styled from 'styled-components';
import { VisibaLink } from '@components/visiba-link.component';
import { LicenceHolderService } from '@services/licence_holder.service';
import { Icon, Button } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { useFeatureFlag } from '@services/feature_flag/use_feature_flag';
import { useUser } from '@services/auth/use_user';
import { AuthService } from '@services/auth/auth.service';
import { RouterService } from '@services/router.service';
import { IdentityApiService } from '@api/identity_api.service';

interface Props {
	__TEMP__onSignIn?: () => void;
	__TEMP__onSignOut?: () => void;
	__TEMP__navigate?(url: string): void;
	__TEMP__changeLanguage?(langCode: string): void;
	trackEvent: (action: AnalyticAction) => void;
}

const elements = {
	hideOnMobile: styled.div`
		display: none;
		user-select: none;
		pointer-events: none;

		${tokens.breakpointQuery.medium} {
			display: contents;
			user-select: unset;
			pointer-events: unset;
		}
	`,
	usernameWrapper: styled(VisibaLink)`
		user-select: none;
		pointer-events: none;

		${tokens.breakpointQuery.medium} {
			user-select: unset;
			pointer-events: unset;
		}
	`,
	userName: styled.div`
		max-width: 100px;
		font: ${tokens.text.body.regular};
		color: ${primitiveColorTokens.primary[1000]};

		display: flex;
		align-items: center;
		padding: 0 ${tokens.spacing.x1};

		${tokens.breakpointQuery.medium} {
			max-width: unset;
		}

		p {
			white-space: pre;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	`,
};

export function CallToAction({
	__TEMP__onSignIn,
	__TEMP__onSignOut,
	__TEMP__navigate,
	__TEMP__changeLanguage,
	trackEvent,
}: Props): JSX.Element {
	const licenceHolderService = useService(LicenceHolderService);
	const routerService = useService(RouterService);
	const authService = useService(AuthService);

	const user = useUser();

	const myAccountUrl = `/${licenceHolderService.alias}/my-account`;

	function handleAuthClick(_: SyntheticEvent<HTMLButtonElement>): void {
		if (user == null) {
			trackEvent('Clicked: Login-button');
			if (__TEMP__onSignIn) {
				__TEMP__onSignIn();

				return;
			}

			// TODO: New patient web sign-in flow

			return;
		}

		handleSignOutClick();
	}

	function handleSignOutClick(): void {
		trackEvent('Clicked: Logout-button');

		authService.terminateActiveIdentity().then(() => {
			const url = '/' + licenceHolderService.alias;
			if (!window.__TEMP__) {
				routerService.navigateByUrl(url);
			} else {
				__TEMP__onSignOut?.();
			}
		});
	}

	function handleNameClick(): void {
		trackEvent('Clicked: Profile');
	}

	return (
		<Layout alignment='leading' direction='row'>
			{user ? (
				<elements.usernameWrapper
					aria-label={I18nAndL10n.translate('PW.Component.Header.Cta.ProfileSettings.Aria', {
						ext: '',
					})}
					to={myAccountUrl}
					__TEMP__to={myAccountUrl}
					__TEMP__navigate={__TEMP__navigate}
					onClick={() => handleNameClick()}
				>
					<elements.userName>
						<elements.hideOnMobile>
							<Icon type='user' />
							<Spacer width={tokens.spacing.x1} />
						</elements.hideOnMobile>

						<Text.p>{user.commonName}</Text.p>
					</elements.userName>
				</elements.usernameWrapper>
			) : null}

			<Spacer width={tokens.spacing.x2} />

			<AuthButton onClick={handleAuthClick} />
		</Layout>
	);
}

function AuthButton(props: { onClick: (event: SyntheticEvent<HTMLButtonElement>) => void }) {
	const user = useUser();
	const licenceHolderService = useService(LicenceHolderService);
	const identityApiService = useService(IdentityApiService);
	const flag = useFeatureFlag('CareExperience.PW.Idp');

	if (flag) {
		if (user != null) {
			return (
				<form
					action={identityApiService.getSignOutPath(`/${licenceHolderService.alias}`)}
					method='post'
					style={{
						display: 'contents',
					}}
				>
					<Button type='submit' size='medium' variant='primary'>
						{withTranslation('Common.Button.SignOut')}
					</Button>
				</form>
			);
		}

		return (
			<Button data-testid='loginButton' size='medium' variant='primary' onClick={props.onClick}>
				{withTranslation('Common.Button.SignIn')}
			</Button>
		);
	}

	return (
		<Button data-testid='loginButton' size='medium' variant='primary' onClick={props.onClick}>
			{user != null ? withTranslation('Common.Button.SignOut') : withTranslation('Common.Button.SignIn')}
		</Button>
	);
}
